import { config } from '../config';
import { isEvidenced, type Evidenced } from './evidence';
import { compareDiscovery, type DiscoveryRow } from './order';
import { ok, reject, type DomainResult } from './result';

/**
 * LOCK-2 — "Suggested 8 = highest `cachedCount` across all buckets, deterministic tie-break by
 * earliest first observation. Given identical observation logs the suggestion is identical every
 * time."
 *
 * The whole requirement is "top `BOARD_CAP` under a **total** order". It reuses `compareDiscovery`
 * rather than restating the chain: DISC-4 and LOCK-2 promise the same ranking, and a second copy of
 * a four-term comparator is a second place for it to drift.
 *
 * Determinism has one non-obvious failure mode this closes. `Array.prototype.sort` is stable in
 * every modern engine, so a *partial* order would preserve input order for ties and the suggestion
 * would then depend on the order rows came back from IndexedDB — which is not something LOCK-2 can
 * promise. The comparator falls through to `id`, so insertion order is never consulted.
 *
 * ── §6.6 (revision 4) — the suggestion is capped by the OBSERVED set ────────
 *
 * PRE-9 pre-selects 49 candidates, so a user who has tapped three rows by `LOCK_NUDGE_MIN` would
 * otherwise be handed three real patterns and **five never-observed ones**, each taking a permanent
 * second-half board slot under INV-4's assign-once rule. Padding a board with patterns nobody saw is
 * the same defect DIST-9 closes one screen later, and it is worse here because the positions are
 * immutable for the rest of the match.
 *
 * **No requirement is withdrawn and no new ID was needed**, which is worth stating so it is not
 * later "corrected" back: the ordering is unchanged, so LOCK-2's *"given identical observation logs
 * the suggestion is identical every time"* holds exactly as before — a suggestion of three is as
 * deterministic as a suggestion of eight. **LOCK-7** already makes a board with fewer than
 * `BOARD_CAP` positioned hypotheses legal, and **LOCK-3** already lets the user swap in any existing
 * hypothesis, so someone who genuinely wants an untapped row on the board can still put it there —
 * deliberately, which is the whole difference.
 *
 * *Alternative considered:* pad to eight and mark the padding — **rejected**, a marked guess is
 * still a board slot spent, and LOCK-7 exists precisely so the board need not be filled.
 */
export function suggestLockSelection<T extends DiscoveryRow & Evidenced>(
  candidates: readonly T[],
): T[] {
  return [...candidates]
    .filter(isEvidenced)
    .sort(compareDiscovery)
    .slice(0, config.BOARD_CAP);
}

const MODULE = 'lock';

/**
 * LOCK-3 — *"The review screen allows swapping any suggestion for any other existing hypothesis
 * before confirm."*
 *
 * Returns the selection with `outgoingId` replaced by `incomingId` **at the same index**, which is
 * what makes the review screen's board order the order LOCK-4 then assigns: §6.2's *"LOCK-4 confirm
 * assigns `0..k−1` in the confirmed order"*. Swapping by removing and appending would silently
 * demote the replacement to the last slot, and the user would have confirmed a board they did not
 * arrange.
 *
 * This runs **before** any position exists — the review screen is reached from Discovery, where every
 * candidate is list-only — so it is a pure list edit and emphatically **not** `board.planSwap`, which
 * is LOCK-6's post-lock composition change and moves a real `boardPosition`. Two functions because
 * they are two different moments with two different invariants; sharing one would be the shortest
 * route to a second `boardPosition` assignment (INV-4).
 *
 * Both rejections are cases the review screen can actually produce — a stale list after a DISC-5
 * addition, and a double-tap on the same candidate — and both would otherwise corrupt the confirmed
 * board silently: a no-op swap leaves the user's chosen replacement off the board, and a duplicate
 * makes `planLockPositions` reject at confirm time, minutes later, with no way to tell which tap
 * caused it.
 */
export function swapSelection(
  selection: readonly string[],
  outgoingId: string,
  incomingId: string,
): DomainResult<string[]> {
  const index = selection.indexOf(outgoingId);
  if (index === -1) {
    return reject(
      MODULE,
      null,
      'ERR_LOCK_NOT_SELECTED',
      'That hypothesis is not in the suggested eight, so there is nothing to swap out.',
      { outgoingId },
    );
  }

  if (outgoingId === incomingId) return ok([...selection]);

  if (selection.includes(incomingId)) {
    return reject(
      MODULE,
      null,
      'ERR_BOARD_DUPLICATE',
      'That hypothesis is already on the board.',
      { incomingId },
    );
  }

  const next = [...selection];
  next[index] = incomingId;
  return ok(next);
}
